import React, { useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";
import styles from "./Protocol.module.css";

interface IProtocolDocumentButtonsProps {
    assemblyId: string;
}

export const ProtocolDocumentButtons = observer(function ProtocolDocumentButtons({
    assemblyId
}: IProtocolDocumentButtonsProps) {
    const { t } = useTranslation();
    const { protocolStore, assemblyStore, agendaitemStore, communicationStore } = useContext(RootStoreContext);
    const protocolFileInputRef = useRef<HTMLInputElement>(null);

    const onDownloadAssemblyProtocol = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await protocolStore.checkAndDownloadAssemblyProtocolPdf(assemblyId);
            await communicationStore.getProtocolOrAgendaitemFile(assemblyId);
        },
        [protocolStore, communicationStore, assemblyId]
    );

    const onUploadAssemblyProtocol = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            e.target.value = "";
            if (!file || !assemblyId) return;

            await agendaitemStore.deleteAssemblyProtocolFiles(assemblyId);

            communicationStore.setSelectedProtocolPdfFile(file);
            await communicationStore.uploadSelectedProtocolPdf(assemblyId);
        },
        [agendaitemStore, communicationStore, assemblyId]
    );

    return (
        <div className={styles.ProtocolDocumentButtonRow}>
            {protocolStore.isProtocolPDFDownloading ? (
                <UI.RotatingSpinner noLogo={true} size={25} />
            ) : (
                <>
                    <UI.Button
                        className={styles.ButtonCreateProtocol}
                        label={t("screens.assemblies.protocolcontent.create_protocol").toString()}
                        onClick={onDownloadAssemblyProtocol}
                        variant="outline-primary"
                        disabled={assemblyStore.isAssemblyReadOnly}
                    />

                    {assemblyStore.currentAssembly?.state === ASSEMBLY_STATE.PROTOCOL_PUBLISHED ? (
                        <div className={styles.PublishedInfo}>
                            {t("screens.assemblies.protocolcontent.protocol_published_info")}
                        </div>
                    ) : undefined}
                </>
            )}
            <UI.Button
                className={styles.ButtonUploadProtocol}
                label={t("screens.assemblies.protocolcontent.upload_protocol").toString()}
                onClick={() => protocolFileInputRef.current?.click()}
                variant="outline-primary"
                disabled={assemblyStore.isAssemblyReadOnly}
            />
            <input
                ref={protocolFileInputRef}
                type="file"
                accept="application/pdf"
                hidden
                disabled={assemblyStore.isAssemblyReadOnly}
                onChange={onUploadAssemblyProtocol}
            />
        </div>
    );
});
